<template>
  <div class="contracts-view">
    <ContractsTopBar
      :park="park"
      :loading="loading"
      :alerts="alerts"
      :search-results="searchResults"
      :search-loading="searchLoading"
      :view="view"
      v-model:search="searchText"
      v-model:active-tab="activeTab"
      :counts="displayCounts"
      @update:view="view = $event"
      @new-contract="onNewContract"
      @import-contracts="showImport = true"
      @search-select="onSearchSelect"
      @open-notifications="onOpenNotifications"
    />

    <div v-if="loading" class="contracts-loading">
      <span class="mdi mdi-loading contracts-loading__spinner"></span>
      <span class="contracts-loading__text">Cargando contratos...</span>
    </div>

    <div v-else class="contracts-view__body">
      <div class="contracts-view__main">
        <ContractsAlertsBanner
          :alerts="contractAlerts"
          @dismiss="onDismissAlert"
          @dismiss-all="onDismissAllAlerts"
          @action="onAlertAction"
        />

        <ContractsListSection
          v-if="view === 'list'"
          :contracts="filteredContractRows"
          :filter-label="activeTab === 'todos' ? '' : activeTab"
          :search-query="searchText"
          @select-contract="onSelectContract"
          @row-action="onRowAction"
          @batch-action="onBatchAction"
          @filter-by-tenant="onFilterByTenant"
        />

        <ContractsCalendarView
          v-else
          :contracts="filteredContractRows"
          @select-contract="onSelectContract"
        />
      </div>

      <Transition name="panel-slide">
        <ContractDetailPanel
          v-if="selectedContractId"
          :contract-id="selectedContractId"
          :contract-list="filteredContractRows"
          :pending-action="pendingPanelAction"
          @close="selectedContractId = null; pendingPanelAction = null"
          @navigate="selectedContractId = $event"
          @refresh="refreshData"
          @action-handled="pendingPanelAction = null"
        />
        <NewContractPanel
          v-else-if="showNewContractPanel"
          :park-id="park?.id"
          @close="showNewContractPanel = false"
          @created="onContractCreated"
        />
      </Transition>
    </div>

    <ContractsImportPanel v-if="showImport && park" :park-id="park.id" @close="showImport = false" @imported="refreshData" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ContractsTopBar from '@/components/Contratos/ContractsTopBar.vue'
import { getParks, getParkById } from '@/services/parks'
import { getParkAlerts } from '@/services/alerts'
import ContractsAlertsBanner from '@/components/Contratos/ContractsAlertsBanner.vue'
import { getContractAlerts, dismissAllContractAlerts, applyEscalation } from '@/services/contracts'
import { dismissAlert } from '@/services/alerts'
import ContractsListSection from '@/components/Contratos/ContractsListSection.vue'
import { searchContracts, getContractCounts, getContractsList } from '@/services/contracts'
import ContractsCalendarView from '@/components/Contratos/ContractsCalendarView.vue'
import ContractDetailPanel from '@/components/Contratos/ContractDetailPanel.vue'
import NewContractPanel from '@/components/Contratos/NewContractPanel.vue'
import ContractsImportPanel from '@/components/Contratos/ContractsImportPanel.vue'

const showImport = ref(false)

const showNewContractPanel = ref(false)
const pendingPanelAction = ref(null)

function onNewContract() {
  selectedContractId.value = null
  showNewContractPanel.value = true
}

async function onContractCreated(contract) {
  showNewContractPanel.value = false
  await refreshData()
  selectedContractId.value = contract.id
}

function onFilterByTenant(tenantId) {
  searchText.value = ''
  searchFilter.value = { type: 'inquilinos', id: tenantId }
}

function onBatchAction({ action, ids }) {
  // TODO: conectar cuando exista backend de acciones en lote
  console.log('Acción en lote:', action, ids)
}

const searchScopedRows = computed(() => {
  if (!searchFilter.value) return contractRows.value
  if (searchFilter.value.type === 'inquilinos') return contractRows.value.filter((r) => r.tenant.id === searchFilter.value.id)
  if (searchFilter.value.type === 'naves') return contractRows.value.filter((r) => r.plot.id === searchFilter.value.id)
  if (searchFilter.value.type === 'contratos') return contractRows.value.filter((r) => r.id === searchFilter.value.id)
  return contractRows.value
})

const selectedContractId = ref(null)

function onSelectContract(row) {
  selectedContractId.value = row.id
}

function onRowAction({  contract }) {
  // Por ahora toda acción de fila abre el panel; los botones específicos (Registrar pago, etc.)
  // ya viven dentro del panel una vez abierto.
  selectedContractId.value = contract.id
}

async function refreshData() {
  if (!park.value) return
  const [countsData, contractRowsData] = await Promise.all([
    getContractCounts(park.value.id),
    getContractsList(park.value.id),
  ])
  counts.value = countsData
  contractRows.value = contractRowsData
  console.log('Total de contratos recibidos:', contractRowsData.length)
  console.log('Contrato en negociación:', contractRowsData.find(r => r.status === 'PENDING'))
}

// Un contrato en negociación que ya está corriendo (inició y sigue vigente) cuenta como activo: sale en Activos / Por vencer /
// Vencidos según su vigencia y, además, en «En negociación». Solo los que aún no inician quedan únicamente en negociación.
function isInForce(row) {
  if (row.status === 'ACTIVE') return true
  return row.status === 'PENDING' && new Date(row.vigencia.startDate).getTime() <= Date.now()
}

function matchesTab(row, tab) {
  if (tab === 'negociacion') return row.status === 'PENDING' || row.activeProcess === 'NEGOTIATION'
  if (tab === 'historicos') return row.status === 'ENDED'
  if (!isInForce(row)) return false
  if (tab === 'vencidos') return row.vigencia.daysRemaining < 0
  if (tab === 'porVencer') return row.vigencia.daysRemaining >= 0 && row.vigencia.daysRemaining <= 90
  if (tab === 'activos') return row.vigencia.daysRemaining > 90
  return true
}

const filteredContractRows = computed(() => {
  const list = searchScopedRows.value
  if (activeTab.value === 'todos') return list
  return list.filter((row) => matchesTab(row, activeTab.value))
})

const displayCounts = computed(() => {
  const list = searchScopedRows.value
  return {
    todos: list.length,
    activos: list.filter((r) => matchesTab(r, 'activos')).length,
    porVencer: list.filter((r) => matchesTab(r, 'porVencer')).length,
    negociacion: list.filter((r) => matchesTab(r, 'negociacion')).length,
    vencidos: list.filter((r) => matchesTab(r, 'vencidos')).length,
    historicos: list.filter((r) => matchesTab(r, 'historicos')).length,
  }
})

const contractRows = ref([])
// agregar getContractsList(parks[0].id) al Promise.all del onMounted, guardar en contractRows.value

const contractAlerts = ref([])

const park = ref(null)
const loading = ref(true)
const alerts = ref([])
const view = ref('list')

const activeTab = ref('activos')
const counts = ref({
  todos: 0, activos: 0, porVencer: 0, negociacion: 0, vencidos: 0, historicos: 0,
})

const searchText = ref('')
const searchResults = ref({ inquilinos: [], naves: [], contratos: [] })
const searchLoading = ref(false)

const route = useRoute()
const router = useRouter()

const CROSS_PAGE_ACTION_MAP = {
  'start-renewal': 'start-renewal',
  'manage-renewal': 'scroll-vigencia',
  'view-tenant-detail': 'scroll-payments',
}

onMounted(async () => {
  try {
    const parks = await getParks()
    if (parks[0]) {
      const [fullPark, alertsData, countsData, contractAlertsData, contractRowsData] = await Promise.all([
        getParkById(parks[0].id),
        getParkAlerts(parks[0].id),
        getContractCounts(parks[0].id),
        getContractAlerts(parks[0].id),
        getContractsList(parks[0].id),
      ])
      park.value = fullPark
      alerts.value = alertsData
      counts.value = countsData
      contractAlerts.value = contractAlertsData
      contractRows.value = contractRowsData
    }
  } finally {
    loading.value = false
  }

  if (route.query.contractId) {
    selectedContractId.value = route.query.contractId
    pendingPanelAction.value = CROSS_PAGE_ACTION_MAP[route.query.actionType] || null
    router.replace({ path: '/Contratos', query: {} })
  }
})

watch(activeTab, () => {
  searchFilter.value = null
})

// Debounce de la búsqueda contra el backend
let debounceHandle = null
watch(searchText, (query) => {
  
  if (debounceHandle) clearTimeout(debounceHandle)

  if (!query.trim()) searchFilter.value = null

  if (!query.trim()) {
    searchResults.value = { inquilinos: [], naves: [], contratos: [] }
    searchLoading.value = false
    return
  }

  searchLoading.value = true
  debounceHandle = setTimeout(async () => {
    if (!park.value) return
    try {
      searchResults.value = await searchContracts(park.value.id, query)
    } finally {
      searchLoading.value = false
    }
  }, 250)
})


// TODO: conectar cuando exista la lista/detalle de contratos
const searchFilter = ref(null)

function onSearchSelect({ type, item }) {
  searchFilter.value = { type, id: item.id }
}

// TODO: conectar cuando exista el panel de notificaciones
function onOpenNotifications() {
  console.log('Abrir notificaciones')
}

async function onDismissAlert(key) {
  contractAlerts.value = contractAlerts.value.filter((a) => a.key !== key)
  await dismissAlert(park.value.id, key)
}

async function onDismissAllAlerts() {
  contractAlerts.value = []
  await dismissAllContractAlerts(park.value.id)
}

async function onAlertAction(alert) {
  if (alert.actionType === 'apply-escalation') {
    const confirmed = confirm(`¿Aplicar nueva renta de ${alert.actionPayload.newRent} MXN?`) // TODO: reemplazar por tu mini modal real
    if (!confirmed) return
    await applyEscalation(alert.actionPayload.contractId, alert.actionPayload.newRent)
    contractAlerts.value = contractAlerts.value.filter((a) => a.key !== alert.key)
    return
  }

  showNewContractPanel.value = false

  if (alert.actionType === 'start-renewal') {
    pendingPanelAction.value = 'start-renewal'
  } else if (alert.actionType === 'manage-renewal') {
    pendingPanelAction.value = 'scroll-vigencia'
  } else if (alert.actionType === 'view-tenant-detail') {
    pendingPanelAction.value = 'scroll-payments'
  } else {
    pendingPanelAction.value = null
  }

  selectedContractId.value = alert.actionPayload.contractId
}
</script>

<style scoped>
.contracts-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.contracts-view__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.contracts-view__main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform 250ms ease-out, opacity 250ms ease-out;
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  transform: translateX(24px);
  opacity: 0;
}

.contracts-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-secondary);
}

.contracts-loading__spinner {
  font-size: 1.8rem;
  animation: contracts-spin 0.8s linear infinite;
}

.contracts-loading__text {
  font-size: 0.85rem;
  font-family: var(--font-body);
}

@keyframes contracts-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>